import { Canvas, Image as FabricImage, Text as FabricText } from 'fabric';

import { Dispatch, SetStateAction } from 'react';
import { Coordinates } from 'types';
import { TemplateMode } from '../PersonalizeCase.types';

const isValidHttpUrl = (urlString: string) => {
  const urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const validateWebsiteUrl = (urlString: string) => {
  return isValidHttpUrl(urlString) || urlString.length !== 0;
};

export const removeObject = (canvas: Canvas | undefined, id: string) => {
  canvas?.forEachObject(function (obj: any) {
    if (obj.id && obj.id === id) canvas.remove(obj);
  });
};

export const addLogoImageToCanvas = (
  canvas: Canvas | undefined,
  logo: File | undefined,
  mode: TemplateMode,
  logoCoordinates: Coordinates | undefined,
  setLogoCoordinates: Dispatch<SetStateAction<Coordinates | undefined>>
) => {
  const reader = new FileReader();
  if (logo) {
    reader.readAsDataURL(logo);
  }
  reader.onload = event => {
    const imageMovedListener = (ev: any) => {
      const target = ev.target;

      setLogoCoordinates({
        top: target.top,
        left: target.left,
        scaleX: target.scaleX,
        scaleY: target.scaleY,
      });
    };

    const imgObj = new Image(100, 200);

    if (typeof event?.target?.result === 'string') {
      imgObj.src = event.target.result;

      // When the picture loads, create the image in Fabric.js
      imgObj.onload = () => {
        const fabricLogoImage = new FabricImage(imgObj, {
          //@ts-ignore
          id: 'logoImageId',
          selectable: mode === 'select' ? false : true,
        });
        fabricLogoImage.setControlsVisibility({ mtr: false });

        fabricLogoImage.scaleToWidth(200);

        if (canvas) {
          if (mode === 'create') {
            canvas.centerObject(fabricLogoImage);
            if (
              fabricLogoImage.top &&
              fabricLogoImage.left &&
              fabricLogoImage.scaleX &&
              fabricLogoImage.scaleY
            ) {
              setLogoCoordinates({
                top: fabricLogoImage.top,
                left: fabricLogoImage.left,
                scaleX: fabricLogoImage.scaleX,
                scaleY: fabricLogoImage.scaleY,
              });
            }
          } else {
            if (logoCoordinates?.top !== undefined)
              fabricLogoImage.top = logoCoordinates.top;
            if (logoCoordinates?.left !== undefined)
              fabricLogoImage.left = logoCoordinates.left;
            if (logoCoordinates?.scaleX !== undefined)
              fabricLogoImage.scaleX = logoCoordinates.scaleX;
            if (logoCoordinates?.scaleY !== undefined)
              fabricLogoImage.scaleY = logoCoordinates.scaleY;
          }

          removeObject(canvas, 'logoImageId');
          canvas.on('object:modified', imageMovedListener);
          canvas.add(fabricLogoImage);
          if (mode !== 'select') {
            canvas.setActiveObject(fabricLogoImage);
          }

          canvas.renderAll();
        }
      };
    }
  };
};

export const addQrCodeImageToCanvas = (
  canvas: Canvas | undefined,
  qrCodeImage: string | undefined,
  mode: TemplateMode,
  qrcodeCoordinates: Coordinates | undefined,
  setqrCodeCoordinates: Dispatch<SetStateAction<Coordinates | undefined>>
) => {
  const reader = new FileReader();

  removeObject(canvas, 'qrCodeImageId');

  if (qrCodeImage) {
    fetch(qrCodeImage).then(async res => {
      const m = await res.blob();
      reader.readAsDataURL(m);
    });
  }

  reader.onload = event => {
    const qrCodeMoveListener = (ev: any) => {
      const target = ev.target;

      if (ev.target.id == 'qrCodeImageId') {
        setqrCodeCoordinates({
          top: target.top,
          left: target.left,
          scaleX: target.scaleX,
          scaleY: target.scaleY,
        });
      }
    };
    const imgObj = new Image(100, 200);
    if (typeof event?.target?.result === 'string') {
      imgObj.src = event.target.result;
    }

    // When the picture loads, create the image in Fabric.js
    imgObj.onload = function () {
      const fabricQrCodeImage = new FabricImage(imgObj, {
        //@ts-ignore
        id: 'qrCodeImageId',
        selectable: false,
      });
      fabricQrCodeImage.setControlsVisibility({ mtr: false });

      if (canvas) {
        if (mode === 'create') {
          fabricQrCodeImage.scaleToWidth(200);

          fabricQrCodeImage.scaleToHeight(50);
          fabricQrCodeImage.scaleToWidth(50);
          fabricQrCodeImage.top = 45;

          canvas.centerObjectH(fabricQrCodeImage);
          if (
            fabricQrCodeImage.top &&
            fabricQrCodeImage.left &&
            fabricQrCodeImage.scaleX &&
            fabricQrCodeImage.scaleY
          ) {
            setqrCodeCoordinates({
              top: fabricQrCodeImage.top,
              left: fabricQrCodeImage.left,
              scaleX: fabricQrCodeImage.scaleX,
              scaleY: fabricQrCodeImage.scaleY,
            });
          }
        } else {
          if (qrcodeCoordinates?.top !== undefined)
            fabricQrCodeImage.top = qrcodeCoordinates.top;
          if (qrcodeCoordinates?.left !== undefined)
            fabricQrCodeImage.left = qrcodeCoordinates.left;
          if (qrcodeCoordinates?.scaleX !== undefined)
            fabricQrCodeImage.scaleX = qrcodeCoordinates.scaleX;
          if (qrcodeCoordinates?.scaleY !== undefined)
            fabricQrCodeImage.scaleY = qrcodeCoordinates.scaleY;
        }

        canvas.add(fabricQrCodeImage);

        canvas.on('object:modified', qrCodeMoveListener);
      }
    };
  };
};

export const addWebUrlToCanvas = (
  canvas: Canvas | undefined,
  webUrl: string,
  mode: TemplateMode,
  weburlCoordinates: Coordinates | undefined,
  setWeburlCoordinates: Dispatch<SetStateAction<Coordinates | undefined>>
) => {
  removeObject(canvas, 'webUrlTextId');
  const webUrlMovedListener = (ev: any) => {
    const target = ev.target;

    if (ev.target.id == 'webUrlTextId') {
      setWeburlCoordinates({
        top: target.top,
        left: target.left,
        scaleX: target.scaleX,
        scaleY: target.scaleY,
      });
    }
  };
  if (canvas) {
    const fabricWebUrlText = new FabricText(webUrl, {
      fontSize: 20,
      fill: '#fff',
      //@ts-ignore
      id: 'webUrlTextId',
      selectable: false,
    });
    fabricWebUrlText.setControlsVisibility({ mtr: false });

    if (mode === 'create') {
      fabricWebUrlText.top = 5;
      canvas.centerObjectH(fabricWebUrlText);
      if (
        fabricWebUrlText.top &&
        fabricWebUrlText.left &&
        fabricWebUrlText.scaleX &&
        fabricWebUrlText.scaleY
      ) {
        setWeburlCoordinates({
          top: fabricWebUrlText.top,
          left: fabricWebUrlText.left,
          scaleX: fabricWebUrlText.scaleX,
          scaleY: fabricWebUrlText.scaleY,
        });
      }
    } else {
      canvas.centerObjectH(fabricWebUrlText);
    }

    canvas.add(fabricWebUrlText);

    canvas.on('object:modified', webUrlMovedListener);
    canvas.renderAll();
  }
};
